import { Link } from "react-router-dom";
import { AirplaneTilt, Factory, Star, ArrowRight } from "@phosphor-icons/react";
import { Container, Overline, SectionTitle, Tag, InternalLinks } from "./Section";

function SectorHeader({ s, i, Icon }) {
  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#9AA5B1]">{String(i + 1).padStart(2, "0")}</span>
        <Icon size={30} weight="light" className="text-[#0B1B3D]" />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-[#0B1B3D] mb-6" data-testid={`secteur-name-${i}`}>
        {s.name}
      </h2>
      <p className="text-xl font-medium text-[#2A2D34] leading-snug mb-6">{s.hook}</p>
      <p className="text-base leading-relaxed text-[#5A6B7C]">{s.text}</p>
    </>
  );
}

function Family({ f, roleLabel, index }) {
  return (
    <div id={`famille-${index}`} data-testid={`famille-${index}`} className="grid lg:grid-cols-12 gap-6 lg:gap-10 py-10 border-t border-[#E2E8F0]">
      <div className="lg:col-span-4">
        <span className="font-heading text-xs font-bold tracking-widest text-[#9AA5B1]">{String.fromCharCode(65 + index)}</span>
        <h3 className="font-heading text-lg font-bold tracking-tight text-[#0B1B3D] mt-2">{f.name}</h3>
      </div>
      <div className="lg:col-span-8">
        <p className="text-base leading-relaxed text-[#2A2D34] mb-6">{f.text}</p>
        {f.domains.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {f.domains.map((d) => <Tag key={d}>{d}</Tag>)}
          </div>
        )}
        {f.roles && (
          <div className="mt-10" data-testid="energie-roles">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#9AA5B1] mb-6">{roleLabel}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {f.roles.map((r, i) => (
                <div key={r.name}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-heading text-xs font-bold tracking-[0.15em] uppercase text-[#0B1B3D]">{r.name}</span>
                    {i < f.roles.length - 1 && <span className="h-px flex-1 bg-[#D5DBE3] hidden lg:block" />}
                  </div>
                  <p className="text-sm text-[#5A6B7C] leading-snug">{r.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Secteurs({ t }) {
  const s = t.secteurs;
  const [aero, indus] = s.items;
  return (
    <section id="secteurs" className="py-24 lg:py-32 bg-[#F4F5F7]">
      <Container>
        <Overline testid="secteurs-overline">{s.overline}</Overline>
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <SectionTitle testid="secteurs-title">{s.title}</SectionTitle>
          <p className="text-base md:text-lg leading-relaxed text-[#5A6B7C] lg:pt-2" data-testid="secteurs-text">{s.text}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <article id={aero.id} data-testid="secteur-aero" className="bg-white p-8 lg:p-12 border-t-2 border-[#0B1B3D] flex flex-col">
            <SectorHeader s={aero} i={0} Icon={AirplaneTilt} />
            <div className="flex flex-wrap gap-2 mt-8">
              {aero.domains.map((d) => <Tag key={d}>{d}</Tag>)}
            </div>
            <p className="mt-auto pt-10 flex items-center gap-2 text-sm font-medium text-[#0B1B3D]" data-testid="aero-note">
              <Star size={16} weight="light" />
              {aero.note}
            </p>
            <Link to={aero.more} data-testid="aero-more-link" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#0B1B3D] hover:text-[#5A6B7C] transition-colors">
              {t.pages.common.more}
              <ArrowRight size={14} weight="light" />
            </Link>
            <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
              <InternalLinks label={s.seeAlso} links={aero.links} testid="aero-links" />
            </div>
          </article>

          <article id={indus.id} data-testid="secteur-indus" className="bg-white p-8 lg:p-12 border-t-2 border-[#0B1B3D] flex flex-col">
            <SectorHeader s={indus} i={1} Icon={Factory} />
            <ol className="mt-8 border-t border-[#E2E8F0]" data-testid="indus-index">
              {indus.families.map((f, i) => (
                <li key={f.name} className="flex items-baseline gap-4 py-3.5 border-b border-[#E2E8F0]">
                  <span className="font-heading text-xs font-bold tracking-widest text-[#9AA5B1]">{String.fromCharCode(65 + i)}</span>
                  <a href={`#famille-${i}`} className="font-heading text-base font-semibold text-[#0B1B3D] hover:text-[#5A6B7C] transition-colors">{f.name}</a>
                </li>
              ))}
            </ol>
            <div className="mt-auto pt-10">
              <div className="pt-6 border-t border-[#E2E8F0]">
                <InternalLinks label={s.seeAlso} links={indus.links} testid="indus-links" />
              </div>
            </div>
          </article>
        </div>

        <div className="bg-white px-8 lg:px-12 mt-8" data-testid="indus-familles">
          {indus.families.map((f, i) => <Family key={f.name} f={f} roleLabel={s.roleLabel} index={i} />)}
        </div>
      </Container>
    </section>
  );
}
